/**
 * Capture serveur d'un order PayPal approuvé — boutique, billetterie, adhésion,
 * complément « upgrade ». Source unique : les quatre tunnels faisaient le même
 * `fetch` recopié, sans JAMAIS regarder la réponse.
 *
 * ⚠️ Pourquoi on lève une erreur (Team Elite Pétanque, 2026-09-28) : une carte
 * refusée par la banque (fonds insuffisants) remontait ici en 402, mais le
 * `fetch` ne regardait pas `res.ok` → la page redirigeait vers « merci » alors
 * qu'aucun euro n'était débité. Lever l'erreur laisse `@donoor/checkout`
 * afficher la raison sur le formulaire, et le donateur réessaie avec une autre
 * carte. Le message est celui de l'API (déjà rédigé pour le donateur).
 */
export async function capturerCommandePaypal(
  apiBaseUrl: string,
  orderId: string,
  organizationId: number | string | null | undefined,
): Promise<{ payerEmail?: string; payerName?: string }> {
  // Textes affichés au donateur si le paiement échoue, traduits via
  // `useNuxtApp().$i18n.t` — JAMAIS `useI18n()` ici.
  //
  // 🔴 PANNE PAYPAL 13→14/10/2026 : cette fonction appelait `useI18n()`. Elle
  // tourne au RETOUR de PayPal (onApprove), hors de tout setup() de
  // composant : vue-i18n y lève « Must be called at the top of a `setup`
  // function » AVANT le fetch. Aucune capture ne partait — plus un seul
  // paiement PayPal (bouton ou carte) n'aboutissait sur le site (adhésions,
  // billetterie, boutique, liens de paiement), sans rien dans les journaux de
  // l'API. Les tests ne le voyaient pas : `tests/setup.ts` remplace
  // `useI18n` par une version qui marche partout.
  const t = useNuxtApp().$i18n.t;
  const messageParDefaut = t("don.capturePaypal.echecPaiementParDefaut");

  let res: Response;
  try {
    res = await fetch(`${apiBaseUrl}/api/public/paypal/capture/${orderId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ organization_id: organizationId }),
    });
  } catch {
    throw new Error(t("don.capturePaypal.erreurConnexion"));
  }
  const data = (await res.json().catch(() => null)) as {
    success?: boolean;
    message?: string;
    error?: { message?: string } | string;
    data?: { payer_email?: string | null; payer_name?: string | null };
  } | null;

  if (!res.ok || data?.success !== true) {
    const erreur = data?.error;
    throw new Error(
      (typeof erreur === "string" ? erreur : erreur?.message) ||
        data?.message ||
        messageParDefaut,
    );
  }

  return {
    payerEmail: data.data?.payer_email ?? undefined,
    payerName: data.data?.payer_name ?? undefined,
  };
}
